/** @jsxRuntime classic */
/** @jsx jsx */

// @ts-ignore
import { jsx } from '@emotion/react'
import { useRef, Fragment, useMemo, ReactElement, useState } from 'react'
import useLayoutEffect from './hooks/layoutEffect'
import range from 'lodash.range'
import uniqueId from 'lodash.uniqueid'
import { set, set2 } from './util/arrays'
// @ts-ignore
import classNames from 'classnames'
// @ts-ignore
import { get, fromMidiSharps } from '@tonaljs/note'
// @ts-ignore
import { fromSemitones } from '@tonaljs/interval'
// @ts-ignore
import { enharmonic } from '@tonaljs/note'
import spanishTheme/*, { Theme } */ from './util/theme'
import getStyles from './styles'
import color from 'color'
import { getKey } from 'keyboard-key'

import { simplifyNoteWithOct } from './util/utils'
import { Scrollbars } from 'react-custom-scrollbars'
import { Card } from '@material-ui/core'

export { spanishTheme/* , Theme*/ }


type Fret = {
  nbr: number;
  color?: string;
  borderColor?: string;
}

type tuningObjectType = {
  instrument: string,
  name: string,
  notes: string,
}

// function hexToRgb(hex : string) {
//   var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
//   return result ? {
//     r: parseInt(result[1], 16),
//     g: parseInt(result[2], 16),
//     b: parseInt(result[3], 16)
//   } : null;
// }

const getScaleNoteIndex = (scaleNotes: string[], note: string) => {
  const i = scaleNotes.findIndex(n => enharmonic(n) === note)
  const ret = i > -1 ? i : scaleNotes.findIndex(n => enharmonic(n) === enharmonic(note))

  // console.log({ scaleNotes, note, ret })

  return ret
}

export function getRenderFingerSpn(tuning: number[], isEnharmonic: boolean, scaleNotes: string[]) {
  return (string: number, fretNbr: number) => {
    const midiSharp = fromMidiSharps(tuning[string] + fretNbr)
    const { name, oct: oct2 } = simplifyNoteWithOct(midiSharp)
    const i = getScaleNoteIndex(scaleNotes, name)

    const { letter, acc, oct } = get(i > -1 ? scaleNotes[i] : midiSharp)
    return (
      <span>
        {letter}
        {acc === '#' ? '♯' : acc === 'b' ? '♭' : ''}
        <sub aria-label={`octave ${oct || oct2}`}>{oct || oct2}</sub>
      </span>
    )
  }
}

const mod = (n: number, m: number) => (m + (n % m)) % m

export function getRenderFingerRelative(tuning: number[], root: number) {
  return (string: number, fret: number) => (
    <Fragment>
      {range(12).map(fromSemitones)[mod(tuning[string] + fret - root, 12)]}
    </Fragment>
  )
}

function Frets(props: {
  className?: string
  currentFrets?: Fret[]
  frets: {
    from: number
    amount: number
  }
  children?: (fretNbr: number) => ReactElement | ReactElement[] | null
  onMouseEnter?: () => void
}) {
  const { from, amount } = props.frets
  return (
    <div
      className={classNames(props.className, 'frets')}
      onMouseEnter={props.onMouseEnter}
    >
      {props.currentFrets !== undefined && (
        <div
          className="fret mute"
          style={{ zIndex: props.currentFrets.find(f => f.nbr === -1) ? 1 : undefined }}
        >
          {props.children?.(-1)}
        </div>
      )}
      {range(from, from + amount + 1).map((fretNbr) => (
        <div className={classNames('fret', { nut: fretNbr === 0 })} key={fretNbr}>
          {props.children?.(fretNbr)}
        </div>
      ))}
    </div>
  )
}

const defaultTuningObject = {
  instrument: 'guitar',
  name: 'Standard',
  notes: 'E2 A2 D3 G3 B3 E4',
}

export default function Guitar(props: {
  id?: string
  className?: string
  strings?: Fret[][]
  frets?: {
    from: number
    amount: number
  }
  lefty?: boolean
  center?: boolean
  renderFinger?: (string: number, fret: number) => JSX.Element
  theme?: any//Theme
  playOnHover?: boolean
  onChange?: (strings: Fret[][]) => void
  onPlay?: (string: number) => void
  tuningObject: tuningObjectType,
  isEnharmonic?: boolean,
  scaleNotes?: string[]
}) {
  const {
    strings = [],
    frets = { from: 0, amount: 22 },
    lefty = false,
    center = false,
    // @ts-ignore
    theme = spanishTheme,
    playOnHover,
    tuningObject = defaultTuningObject,
    isEnharmonic = false,
    scaleNotes = [],
    // @ts-ignore
    renderFinger = getRenderFingerSpn(tuningObject.notes.split(' ').reverse().map(n => get(n).midi), isEnharmonic, scaleNotes),
  } = props
  const id = useMemo(() => props.id || uniqueId('guitar-'), [props.id])
  const styles = useMemo(() => getStyles(theme), [theme])
  const ref = useRef(null as HTMLDivElement | null)
  // const focusString = (string: number, fret: number = strings[string]) =>
  //   ref.current
  //     ?.querySelector?./*<HTMLInputElement>*/(
  //       `input[name="${id}-string-${string}"][value="${fret}"]`
  //     )
  // // @ts-ignore
  //     ?.focus()
  // const releaseString = (string: number) =>
  //   pressString(string, strings[string] === 0 ? -1 : 0)
  const pressString = (string: number, fret: Fret) => {
    // focusString(string, fret)

    const index = strings[string].findIndex(f => f.nbr === fret.nbr)

    props.onChange?.(set(strings, string, index > -1 ? [
      ...strings[string].slice(0, index),
      ...strings[string].slice(index + 1)
    ] : [...strings[string], fret]))
  }
  const getNavigationDelta = (e: KeyboardEvent) => {
    switch (getKey(e)) {
      case 'ArrowDown':
        return { x: 0, y: 1 }
      case 'ArrowUp':
        return { x: 0, y: -1 }
      case 'ArrowRight':
        return { x: lefty ? -1 : 1, y: 0 }
      case 'ArrowLeft':
        return { x: lefty ? 1 : -1, y: 0 }
      default:
        return null
    }
  }
  useLayoutEffect(() => {// FIXME
    // const fretsNode = ref.current
    // if (center && fretsNode) {
    //   const children = fretsNode.querySelectorAll('.fret')
    //   const pressedFrets = strings.filter((fret) => fret > 0)
    //   const minFret = Math.min.apply(Math, pressedFrets)
    //   const maxFret = Math.max.apply(Math, pressedFrets)
    //   const toFret = minFret + Math.floor((maxFret - minFret) / 2)
    //   const fretNode = children[toFret] as HTMLElement | undefined
    //   if (fretNode) {
    //     fretsNode.scrollLeft =
    //       fretNode.offsetLeft -
    //       fretsNode.offsetWidth / 2 +
    //       fretNode.offsetWidth / 2
    //   }
    // }
  }, [ref, strings, center, lefty])
  const [focusedString, setFocusedString] = useState(0)
  return (
    <Card elevation={3} style={{ margin: 64, borderRadius: 10 }}>
      {/* <Scrollbars
        className='react-guitar-scrollbar'
        autoHeight
        autoHeightMax={800}
        // renderTrackHorizontal={(el) => {
        //   console.log({ el })
        //   return el
        // }}
      > */}
        <div
          id={id}
          ref={ref}
          // @ts-ignore
          css={styles}
          className={classNames('guitar', { lefty }, props.className)}
          // onKeyDown={(e) => {
          //   const delta = getNavigationDelta(e.nativeEvent)
          //   if (delta) {
          //     const string = mod(focusedString + delta.y, strings.length)
          //     const fret = strings[string] + delta.x
          //     pressString(
          //       string,
          //       fret > frets.from + frets.amount
          //         ? -1
          //         : fret < -1
          //         ? frets.from + frets.amount
          //         : fret
          //     )
          //     e.preventDefault()
          //   }
          // }}
        >
          <div className="sr-only">
            This is a guitar with {strings.length} strings and {frets.amount} frets,
            starting from {frets.from}. Its theme describes it as:{' '}
            {theme.description}.
            {props.onChange && (
              <span>
                Once you focus on a string you will be able to navigate strings and
                frets using the arrow keys.
              </span>
            )}
            {props.onPlay && (
              <span>
                When a specific string is focused you can play it by pressing 'p'.
              </span>
            )}
          </div>
          {/* <div className="sr-only" role="status">// TODO
            Current fretting: {strings.join(', ')}.
          </div> */}
          <div className="strings">
            <Frets className="fretboard" frets={frets}>
              {theme.fret.marker
                ? (fret) => (
                    <div className="marker">{theme.fret.marker?.(fret)}</div>
                  )
                : undefined}
            </Frets>
            {strings.map((currentFrets, string) => (
              <fieldset key={string} className="string">
                <legend className="sr-only">
                  String {string + 1}.{' '}
                  {currentFrets.find(f => f.nbr === -1) && 'This string is muted.'}
                </legend>
                <Frets
                  currentFrets={currentFrets}
                  frets={frets}
                  onMouseEnter={() => playOnHover && props.onPlay?.(string)}
                >
                  {(fretNbr) => {
                    // console.log({ string, fret, visible: currentFrets.includes(fret) })

                    const fret = currentFrets.find(f => f.nbr === fretNbr)
                    // const rgbColor = fret && fret.color ? hexToRgb(fret.color) : null

                    const checked = currentFrets.find(f => f.nbr === fretNbr) !== undefined

                    return (
                    <label>
                      <span className="sr-only">
                        Fret {fretNbr} {fretNbr === -1 && '(Mute)'}.
                      </span>
                      {fretNbr >= 0 && (
                        <span
                          className="actual-string"
                          style={{
                            opacity: currentFrets.find(f => f.nbr === -1) ? 0.2 : 1,
                            borderBottom: `solid 0.2em ${color(
                              theme.string.color(string)
                            ).darken(0.35)}`,
                            backgroundColor: theme.string.color(string),
                          }}
                        />
                      )}
                      <input
                        disabled={!props.onChange}
                        type="radio"
                        name={`${id}-string-${string}`}
                        value={fretNbr}
                        checked={checked}
                        onChange={(e) => {
                          console.log('onChange')
                          pressString(string, { nbr: fretNbr })// FIXME
                          // // @ts-ignore
                          // props.onChange?.(set(strings, string, [...strings[string], fret]))
                          e.target.focus()
                        }}
                        onClick={() => {
                          console.log('onClick')
                          checked && pressString(string, { nbr: fretNbr })
                        }}
                        // onKeyDown={(e) => {
                        //   switch (e.keyCode) {
                        //     case 80:
                        //       props.onPlay?.(string)
                        //       break
                        //     case 13:
                        //       releaseString(string)
                        //       e.preventDefault()
                        //   }
                        // }}
                        onFocus={() => setFocusedString(string)}
                      />
                      <span className={classNames('finger', { visible: checked })} style={{ backgroundColor: fret?.color, borderBottomColor: fret?.borderColor }}>
                        {renderFinger?.(string, fretNbr === -1 ? 0 : fretNbr)}
                      </span>
                    </label>
                  )}}
                </Frets>
              </fieldset>
            ))}
          </div>
          <Frets className="frame" frets={frets}>
            {(fret) =>
              fret !== 0 ? <span className="counter">{fret}</span> : null
            }
          </Frets>
        </div>
      {/* </Scrollbars> */}
    </Card>
  )
}
